import React from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { BiArrowBack } from 'react-icons/bi';
import { HiOutlineMail } from 'react-icons/hi';

import FormUrl from 'partial/form/FormUrl';
import Card from 'partial/components/Card';
import FormInput from 'partial/form/FormInput';
import Button from 'partial/components/Button';
import FormTextArea from 'partial/form/FormTextArea';
import FormDatasetOptions from 'core-modules/dataset/components/FormDatasetOptions';
import Form, {
  ErrorLabel,
  ErrorOrDescription,
  setError422,
} from 'partial/form/Form';
import FormPhoneNumber from 'partial/form/FormPhoneNumber';
import FormFileDnd from 'partial/form/FormFileDnd';
import { objectToString } from 'helper';

import {
  useFormGeneralInfo,
  useGeneralInfo,
  useSaveGeneralInfo,
} from '../hooks';
import { transformGeneralInfo } from '../transformers';

const initialValues = transformGeneralInfo();

const validationSchema = yup.object().shape({
  logo: yup.array().min(1, 'Required'),
  name: yup.string().required('Required'),
  website_url: yup.string(),
  industry: yup.string().required('Required'),
  customer_service_number: yup
    .string()
    .required('Required')
    .matches(/^\+673[\s]?\d{3}[\s]?\d{4}$/, 'Invalid format'),
  email: yup.string().required('Required').email('The email format is invalid'),
  // business_address: yup.string().required('Required'),
  // business_geoloc: yup.string().required('Required'),
  district: yup.string().required('Required'),
  street: yup.string(),
  house_no: yup.string(),
  building_name: yup.string(),
  unit_no: yup.string(),
  postal_code: yup
    .string()
    .required('Required')
    .min(6, 'At least 6 characters')
    .max(6, 'Maximum Postal Code is at 6 characters')
    .matches(/^[A-Za-z0-9]+$/, 'Invalid format'),
  business_description: yup
    .string()
    .required('Required')
    .max(100, 'Title must be 100 characters only.'),
  business_type: yup.string(),
});

function GeneralInfo({ onNext, onBack }) {
  const [isLoading, submit] = useSaveGeneralInfo();
  const [validateOnChange, setValidateOnChange] = React.useState(false);
  const [, data] = useGeneralInfo();
  const {
    values,
    setFieldValue,
    handleSubmit,
    errors,
    setValues,
    setFieldError: onSetFieldError,
  } = useFormik({
    initialValues: { ...initialValues, ...data },
    validationSchema,
    onSubmit: async (form, { setFieldError }) => {
      const mobileNumber = form?.customer_service_number.replace(/\s+/g, '');
      const arg = {
        ...form,
        logo: objectToString(form?.logo),
        customer_service_number: mobileNumber,
      };
      submit(arg, onNext, (err) => {
        setError422(err, setFieldError);
      });
    },
    validateOnChange,
  });
  useFormGeneralInfo(setValues);

  return (
    <Card className="relative">
      <span className="absolute top-0 left-0 m-4">
        <Button
          transparent
          size="lg"
          icon={BiArrowBack}
          onClick={() => onBack(data)}
        >
          Back
        </Button>
      </span>
      <Form
        className="space-y-4 max-w-3xl mx-auto p-12"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
        error={errors}
      >
        <div className="space-y-3 mb-5">
          <div className="title-h1 text-center">General Information</div>
          <p className="text-center text-base text-gray-800">
            Help us get to know you better. These are questions we ask all new
            businesses joining DST Biller Portal.
          </p>
        </div>
        <hr />
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col">
              <div className="flex flex-col">
                <span className="text-xs font-medium mb-1 text-gray-500">
                  Upload Company Logo
                </span>
                <span className="text-xs font-light mb-2 text-gray-500">
                  Accepted files are .jpg or .png with a file size lower than
                  10MB.
                </span>
              </div>
              <FormFileDnd
                className="h-[170px]"
                name="logo"
                onSetFieldValue={setFieldValue}
                onSetFieldError={onSetFieldError}
                value={values.logo}
                error={errors?.logo}
                imagesOnly
              />
              <ErrorLabel name="logo" />
            </div>
            <div className="space-y-3">
              <div>
                <FormInput
                  name="name"
                  onSetFieldValue={setFieldValue}
                  value={values.name}
                  error={errors?.name}
                  label="Business Name"
                />
                <ErrorOrDescription
                  name="name"
                  label="The name of your business that your customers will recognize. It may be different from the reigstered entity name."
                />
              </div>
              <div>
                <FormPhoneNumber
                  name="customer_service_number"
                  onSetFieldValue={setFieldValue}
                  value={values.customer_service_number}
                  error={errors?.customer_service_number}
                  placeholder="+673 XXX XXXX"
                  label="Customer Service Number"
                />
                <ErrorOrDescription
                  name="customer_service_number"
                  label="This is a landline or mobile number your customer can contact."
                />
              </div>
            </div>
          </div>
          <div>
            <FormInput
              icon={HiOutlineMail}
              name="email"
              label="Email Address"
              placeholder="username@domain.com"
              type="email"
              onSetFieldValue={setFieldValue}
              value={values?.email || ''}
              error={errors?.email}
            />
            <ErrorLabel name="email" />
          </div>
          <div>
            <FormDatasetOptions
              code="INDUSTRY"
              name="industry"
              onSetFieldValue={setFieldValue}
              value={values.industry}
              error={errors?.industry}
              label="Industry"
            />
            <ErrorLabel name="industry" />
          </div>
          <div>
            <FormUrl
              name="website_url"
              onSetFieldValue={setFieldValue}
              value={values.website_url}
              error={errors?.website_url}
              optional
              label="Website or social media page of your business"
              placeholder="domainname/path"
            />
            <ErrorOrDescription
              name="website_url"
              label="Provide the URL of your website or any social media page where you conduct business with your customers. Business pages and profiles are preferred."
            />
          </div>
          <hr />
          <div className="text-gray-500 font-semibold">Business Address</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FormDatasetOptions
                code="DISTRICT"
                name="district"
                onSetFieldValue={setFieldValue}
                value={values.district}
                error={errors?.district}
                label="District/Mukim/Village"
              />
              <ErrorLabel name="district" />
            </div>
            <div>
              <FormInput
                name="street"
                onSetFieldValue={setFieldValue}
                value={values.street}
                error={errors?.street}
                optional
                label="Street/Simpang"
                placeholder="Enter Street/ Simpang"
              />
              <ErrorLabel name="street" />
            </div>
            <div>
              <FormInput
                name="house_no"
                onSetFieldValue={setFieldValue}
                value={values.house_no}
                error={errors?.house_no}
                optional
                label="Block/House No."
                placeholder="Enter Block/ House No."
              />
              <ErrorLabel name="house_no" />
            </div>
            <div>
              <FormInput
                name="building_name"
                onSetFieldValue={setFieldValue}
                value={values.building_name}
                error={errors?.building_name}
                optional
                label="Building Name"
                placeholder="Enter Building Name"
              />
              <ErrorLabel name="building_name" />
            </div>
            <div>
              <FormInput
                name="unit_no"
                onSetFieldValue={setFieldValue}
                value={values.unit_no}
                error={errors?.unit_no}
                optional
                label="Floor/Unit No."
                placeholder="Enter Floor/ Unit No."
              />
              <ErrorLabel name="unit" />
            </div>
            <div>
              <FormInput
                name="postal_code"
                onSetFieldValue={setFieldValue}
                value={values.postal_code}
                error={errors?.postal_code}
                label="Postal Code"
                placeholder="Enter Postal Code"
              />
              <ErrorLabel name="postal_code" />
            </div>
          </div>
          <hr />
          <div>
            <FormTextArea
              name="business_description"
              onSetFieldValue={setFieldValue}
              value={values.business_description}
              error={errors?.business_description}
              label="Product Description and nature of your business"
              maxLength={100}
              withCounter
              placeholder="Description of your business here"
            />
            <ErrorOrDescription
              name="business_description"
              label="Describe the products or services that you sell, your customers and when you collect payments from your customers (e.g., at store checkout, weekly, day after providing the service)."
            />
          </div>
        </div>
        <div className="mt-3 border-t pt-3 flex justify-end">
          <Button
            primary
            size="lg"
            type="submit"
            disabled={isLoading}
            onClick={() => {
              setValidateOnChange(true);
            }}
          >
            Continue to Business Information
          </Button>
        </div>
      </Form>
    </Card>
  );
}

GeneralInfo.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default GeneralInfo;
